import { useState } from 'react';
import {
  IconContainer,
  Panel,
  RadioInput,
  Text,
  Tooltip,
  //RadioGroup as RadioChoise,
} from '@epam/promo';

import { useTaskStore } from 'store/task';
import { TagsInput } from 'components/common/TagsInput/TagsInput';
import {
  Task,
  VALIDATION_TOOLTIP_TEXT,
  VALIDATION_TYPE,
  ValidationTooltipText,
  ValidationType,
} from 'models/task.model';
import css from '../FormBody.module.scss';
import { TAG_MAX_LENGTH } from './constants';
import { InlineLabelInput } from '../../../common';
import { QuestionValidator } from './QuestionsValidator';

export const RadioGroup = ({ task }: { task: Task }) => {
  const {
    answers: { containsOneOf, matchesOneOf, predefinedOptions },
    validator,
    status,
  } = task;
  const {
    errors,
    draft: draftTask,
    actions: { setDraftAnswer, setDraftValidator },
  } = useTaskStore((state) => state);
  const [containsInputValue, setContainsInputValue] = useState('');
  const [matchesInputValue, setMatchesInputValue] = useState('');

  const pushToContains = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const inputValue = containsInputValue.trim();

    if (
      inputValue &&
      !containsOneOf.includes(inputValue) &&
      event.key === 'Enter' &&
      inputValue.length <= TAG_MAX_LENGTH
    ) {
      setDraftAnswer({
        matchesOneOf,
        containsOneOf: [...containsOneOf, inputValue],
        predefinedOptions,
      });
      setContainsInputValue('');
    }

    if (event.key === 'Backspace' && inputValue === '') {
      const tagValue = containsOneOf[containsOneOf.length - 1];
      const containsTags = containsOneOf.filter((tag) => tag !== tagValue);

      setDraftAnswer({
        matchesOneOf,
        containsOneOf: containsTags,
        predefinedOptions,
      });

      setContainsInputValue(tagValue);
    }
  };

  const pushToMatches = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const inputValue = matchesInputValue.trim();

    if (
      inputValue &&
      !matchesOneOf.includes(inputValue) &&
      event.key === 'Enter' &&
      inputValue.length <= TAG_MAX_LENGTH
    ) {
      setDraftAnswer({
        matchesOneOf: [...matchesOneOf, inputValue],
        containsOneOf,
        predefinedOptions,
      });
      setMatchesInputValue('');
    }

    if (event.key === 'Backspace' && inputValue === '') {
      const tagValue = matchesOneOf[matchesOneOf.length - 1];
      const matchesTags = matchesOneOf.filter((tag) => tag !== tagValue);

      setDraftAnswer({
        matchesOneOf: matchesTags,
        containsOneOf,
        predefinedOptions,
      });

      setMatchesInputValue(tagValue);
    }
  };

  const removeTagFromContains = (tag: string): void => {
    const containsList = containsOneOf.filter((answer) => answer !== tag);

    setDraftAnswer({
      matchesOneOf,
      containsOneOf: containsList,
      predefinedOptions,
    });
  };

  const removeTagFromMatches = (tag: string): void => {
    const matchesList = matchesOneOf.filter((answer) => answer !== tag);

    setDraftAnswer({
      matchesOneOf: matchesList,
      containsOneOf,
      predefinedOptions,
    });
  };

  const isNotAvailableStatus =
    status === 'PENDING_APPROVAL' || status === 'APPROVED';

  return (
    <InlineLabelInput isRequired align="top" label="Validator">
      <Text cx={css.form__text_italic}>
        *Choose one of the options to validate user&apos;s answers in the bot.
      </Text>

      <Panel cx={css.radio__inputs}>
        {Object.keys(VALIDATION_TYPE).map((validation) => (
          <div key={validation}>
            <div className={css.radio__label}>
              <RadioInput
                cx={{
                  [css['radio-input__error']]:
                    errors.answers && validator === validation,
                  [css.form__input_disabled]: isNotAvailableStatus,
                }}
                isDisabled={isNotAvailableStatus}
                isRequired={!!errors.answers}
                label={
                  <div className={css.radio__label}>
                    <span className={css.radio__label__text}>
                      {VALIDATION_TYPE[validation as ValidationType]}
                    </span>
                    <Tooltip
                      content={
                        VALIDATION_TOOLTIP_TEXT[
                          validation as ValidationTooltipText
                        ]
                      }
                      placement="top">
                      <IconContainer cx={css.icon} />
                    </Tooltip>
                  </div>
                }
                rawProps={{ 'data-testid': `validator-${validation}` }}
                value={validator === validation}
                onValueChange={() => {
                  setDraftValidator(validation as ValidationType);
                }}
              />
            </div>
            {validation === 'CONTAINS_ONE_OF' && (
              <TagsInput
                errorMessage={errors.answers}
                inputValue={containsInputValue}
                isDisabled={isNotAvailableStatus}
                isOptionSelected={validator === validation}
                pushToList={pushToContains}
                removeItemFromList={removeTagFromContains}
                tagsList={containsOneOf}
                updateInputValue={setContainsInputValue}
              />
            )}

            {validation === 'MATCHES_ONE_OF' && (
              <TagsInput
                errorMessage={errors.answers}
                inputValue={matchesInputValue}
                isDisabled={isNotAvailableStatus}
                isOptionSelected={validator === validation}
                pushToList={pushToMatches}
                removeItemFromList={removeTagFromMatches}
                tagsList={matchesOneOf}
                updateInputValue={setMatchesInputValue}
              />
            )}

            {validation === 'PREDEFINED_OPTIONS' && <QuestionValidator />}
          </div>
        ))}
      </Panel>
    </InlineLabelInput>
  );
};
